import type { FC, MouseEvent, MutableRefObject, ReactNode } from 'react';
import { cx } from './cx';
import type { PluginIconName } from './icon-svg/plugin-icon';
import { PluginIcon } from './icon-svg/plugin-icon';
import { Popover } from './popover-tooltip';

export type ItemClick = (
  item: ListViewItem,
  index: number,
  event: React.MouseEvent,
  setOpen?: (open: boolean) => void
) => void;

export interface Props {
  items: ListViewItem[];
  className?: string;
  itemClassName?: string;
  itemClick?: ItemClick;

  setOpen?: (open: boolean) => void;
  activityKey?: string;
  portalRoot?: HTMLElement | null | MutableRefObject<HTMLElement | null>;

  disableNextModal?: boolean;

  header?: ReactNode;
  footer?: ReactNode;
  titleClassName?: string;
  listClassName?: string;
}

export interface ListViewItem {
  icon?: PluginIconName;
  key: string;
  title: string;
  checked?: boolean;

  onClick?: (item: ListViewItem, event: React.MouseEvent) => void;
  render?: (item: ListViewItem) => ReactNode;
  rightContent?: ReactNode;
  subContent?: ReactNode;
  value?: any;
}

export const ListView: FC<Props> = (props) => {
  const {
    items,
    className,
    itemClassName,
    itemClick,
    setOpen,
    activityKey,
    portalRoot,
    disableNextModal = false,
    header,
    footer,
    titleClassName,
    listClassName,
  } = props;

  return (
    <div className={cx('min-w-[150px] rounded', !disableNextModal && 'next-modal', className)}>
      {header}

      <ul className={cx('flex flex-col py-2 text-black', listClassName)}>
        {items.map((item, index) => {
          const { icon, checked, key, title, onClick, render, rightContent, subContent } = item;

          const handleClick = (event: MouseEvent) => {
            if (onClick) {
              onClick?.(item, event);
            }

            itemClick?.(item, index, event, setOpen);
          };

          const content = (
            <>
              {icon ? (
                <div className={cx('flex items-center space-x-2', titleClassName)}>
                  {<PluginIcon name={icon} size="middle" />}
                  <span className="text-ellipsis">{title}</span>
                </div>
              ) : (
                <span className="text-ellipsis">{title}</span>
              )}

              {activityKey && key === activityKey && <PluginIcon name="IcCheck" size="middle" />}

              {checked && <PluginIcon name="IcCheck" size="middle" />}

              {rightContent}
            </>
          );

          return (
            <li
              key={key}
              onClick={handleClick}
              className={cx(
                'flex justify-between hover:bg-black1 min-h-[32px] hover:bg-black1/5 cursor-pointer items-center text-t2 text-black duration-200 group rounded',
                itemClassName,
                subContent ? 'px-0' : 'px-4'
              )}
            >
              {render && render(item)}

              {subContent && (
                <Popover
                  popup={subContent}
                  portalRoot={portalRoot}
                  offset={10}
                  placement="right"
                  className="w-full h-full border-none py-0 space-x-2 justify-between px-4"
                >
                  {content}
                </Popover>
              )}

              {!render && !subContent && content}
            </li>
          );
        })}
      </ul>

      {footer}
    </div>
  );
};
